import React from 'react';
import { Button } from '../ui/button';
import { DynamicTable } from '../common/DynamicTable';
import { Pencil, Trash2, User } from 'lucide-react';

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  role: 'SuperAdmin' | 'Admin' | 'Atendente' | 'Usuario';
  ativo: boolean;
  empresaId: string;
  empresaNome?: string;
  filas?: Array<{ id: string; nome: string }>;
  dataCriacao?: string;
  dataAtualizacao?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

interface UsuariosTableProps {
  usuarios: Usuario[];
  loading: boolean;
  total: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSort: (field: Extract<keyof Usuario, string>, order: 'asc' | 'desc') => void;
  onSearch: (searchTerm: string) => void;
  onRowClick: (row: Usuario) => void;
  onEdit: (usuario: Usuario) => void;
  onDelete: (usuario: Usuario) => void;
  canEdit: boolean;
  showEmpresaColumn?: boolean;
}

const roleLabels: Record<string, string> = {
  'SuperAdmin': 'Super Admin',
  'Admin': 'Administrador',
  'Atendente': 'Atendente',
  'Usuario': 'Usuário'
};

export const UsuariosTable: React.FC<UsuariosTableProps> = ({
  usuarios,
  loading,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onSort,
  onSearch,
  onRowClick,
  onEdit,
  onDelete,
  canEdit,
  showEmpresaColumn = true,
}) => {
  const columns = [
    {
      id: 'nome' as Extract<keyof Usuario, string>,
      label: 'Nome',
      sortable: true,
      format: (value: string, row: Usuario) => (
        <div className="flex items-center min-w-0">
          <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 mr-3">
            <User className="h-5 w-5 text-gray-500" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-gray-900 dark:text-white">{value}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400 break-all">{row.email}</div>
          </div>
        </div>
      )
    },
    {
      id: 'role' as Extract<keyof Usuario, string>,
      label: 'Função',
      format: (value: string) => roleLabels[value] || value
    },
    ...(showEmpresaColumn ? [
      {
        id: 'empresaNome' as Extract<keyof Usuario, string>,
        label: 'Empresa',
        sortable: true
      }
    ] : []),
    {
      id: 'filas' as any, // Custom field not in the Usuario interface
      label: 'Filas',
      format: (_: any, row: Usuario) => (
        <div className="flex flex-wrap gap-1">
          {row.filas?.map(fila => (
            <span
              key={fila.id}
              className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
            >
              {fila.nome}
            </span>
          )) || <span className="text-gray-400">-</span>}
        </div>
      )
    },
    {
      id: 'ativo' as Extract<keyof Usuario, string>,
      label: 'Status',
      align: 'center' as const,
      format: (value: boolean) => (
        <span className={`px-2 py-1 rounded-full text-xs ${value ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
          {value ? 'Ativo' : 'Inativo'}
        </span>
      )
    },
    {
      id: 'dataCriacao' as Extract<keyof Usuario, string>,
      label: 'Criado em',
      format: (value: string) => value ? new Date(value).toLocaleDateString('pt-BR') : '-'
    },
    {
      id: 'actions' as any, // 'actions' is a special column ID
      label: 'Ações',
      align: 'center' as const,
      format: (_: any, row: Usuario) => (
        <div className="flex justify-center space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(row);
            }}
            className="dark:text-white dark:bg-slate-700"
            disabled={!canEdit}
          >
            <Pencil className="h-4 w-4 mr-1" />
            Editar
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row);
            }}
            className="dark:bg-red-600 dark:hover:bg-red-700"
            disabled={!canEdit || row.role === 'SuperAdmin'}
            title={row.role === 'SuperAdmin' ? 'Não é possível excluir um Super Admin' : ''}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Excluir
          </Button>
        </div>
      )
    }
  ];

  return (
    <DynamicTable
      columns={columns}
      data={usuarios}
      loading={loading}
      total={total}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      onSort={onSort}
      onSearch={onSearch}
      onRowClick={onRowClick}
      mobileCards
      searchPlaceholder="Pesquisar usuários..."
    />
  );
};
